import * as R from "ramda";

import type { ProductId } from "../specs/product";
import type { AssortmentItem, ManualAssortmentItem, WaWiAssortmentItem } from "../types/assortment";

const uniqAssortmentItems = <T extends AssortmentItem>(...assortmentItemsList: T[][]): T[] => {
  const newAssortment = new Map();

  assortmentItemsList.forEach(assortmentItems =>
    assortmentItems.forEach(assortmentItem =>
      newAssortment.set(`productId:${assortmentItem.productId}_variantId:${assortmentItem.variantId}`, assortmentItem)
    )
  );

  return Array.from(newAssortment.values());
};

const withoutAssortmentItems = <T extends AssortmentItem>(assortmentItemsToRemove: T[], assortment: T[]): T[] => {
  const newAssortment = new Map();

  assortment.forEach(assortmentItem => {
    newAssortment.set(`productId:${assortmentItem.productId}_variantId:${assortmentItem.variantId}`, assortmentItem);
  });

  assortmentItemsToRemove.forEach(assortmentItemToRemove => {
    newAssortment.delete(`productId:${assortmentItemToRemove.productId}_variantId:${assortmentItemToRemove.variantId}`);
  });

  return Array.from(newAssortment.values());
};

export const addProductIdsToManualAssortment = (
  assortmentItems: AssortmentItem[],
  currentAssortment: AssortmentItem[]
): AssortmentItem[] => {
  return uniqAssortmentItems(assortmentItems, currentAssortment);
};

export const deleteProductIdsFromManualAssortment = (
  assortmentItems: AssortmentItem[],
  currentAssortment: AssortmentItem[]
): AssortmentItem[] => withoutAssortmentItems(assortmentItems, currentAssortment);

export const addProductIdsToWawiAssortment = (
  wawiResults: WaWiAssortmentItem[],
  currentAssortment: WaWiAssortmentItem[]
): WaWiAssortmentItem[] => {
  return uniqAssortmentItems(wawiResults, currentAssortment);
};

export const deleteProductIdsFromWawiAssortment = (
  wawiResults: AssortmentItem[],
  currentAssortment: AssortmentItem[]
): AssortmentItem[] => withoutAssortmentItems(wawiResults, currentAssortment);

export const getCurrentAssortmentFromAssortmentItems = (assortmentItem: AssortmentItem[]): ProductId[] =>
  R.uniq(R.map(R.prop("productId"), assortmentItem));

export const getManualAssortmentItemFromProductIds = (productIds: ProductId[]): AssortmentItem[] =>
  productIds.map(productId => ({ productId, variantId: null }));

export const isInAssortment = (
  assortment: AssortmentItem[],
  productId: ProductId,
  variantId?: ProductId | null
): boolean =>
  assortment.some(
    assortmentItem =>
      assortmentItem.productId === productId && (assortmentItem.variantId === variantId || variantId === undefined)
  );

export const isWaWiAssortmentItem = (assortmentItem: AssortmentItem): assortmentItem is WaWiAssortmentItem =>
  (assortmentItem as WaWiAssortmentItem).wawiProductAvailabilityVariant !== undefined;

export const isManualAssortmentItem = (assortmentItem: AssortmentItem): assortmentItem is ManualAssortmentItem =>
  (assortmentItem as WaWiAssortmentItem).wawiProductAvailabilityVariant === undefined;

export type AssortmentItemLookup<T> = (productId: ProductId, variantId: ProductId | null) => T | undefined;

/**
 * Returns a function that finds the item of a product and variant. If several items match, the first one wins.
 *
 * The items are put into a Map once, so every lookup is fast. Searching the list with `find` for every variant
 * would take variants × items steps, e.g. 180,000 × 50,000 for a big brand with a big assortment.
 */
export const createAssortmentItemLookup = <T extends { productId: ProductId; variantId: ProductId | null }>(
  items: T[]
): AssortmentItemLookup<T> => {
  const itemsByProductId = new Map<ProductId, Map<ProductId | null, T>>();

  items.forEach(item => {
    const itemsByVariantId = itemsByProductId.get(item.productId) ?? new Map<ProductId | null, T>();
    if (!itemsByVariantId.has(item.variantId)) {
      itemsByVariantId.set(item.variantId, item);
    }
    itemsByProductId.set(item.productId, itemsByVariantId);
  });

  return (productId, variantId) => itemsByProductId.get(productId)?.get(variantId);
};
